<script lang="ts">
  import { inertia } from '../utils/inertia'
  import type { SerializedPage, SerializedTagsBlock, SerializedPreviewImageBlock } from '../types'
  import { expandClick } from '../utils/expandClick'
  import { CATEGORIES, type Category } from '../categories'
  import Icon from './Icon.svelte'
  import TagPill from './TagPill.svelte'
  import { faArrowRight } from '@fortawesome/free-solid-svg-icons'
  import MirroredIcon from './MirroredIcon.svelte'
  import { getI18n } from '../i18n'

  // `headingLevel` follows the heading above the cards; `priority` loads the image first, for a card at the top of the page.
  let {
    page,
    category,
    headingLevel = 'h3',
    priority = false,
  }: { page: SerializedPage; category: Category; headingLevel?: 'h2' | 'h3'; priority?: boolean } = $props()

  const i18n = getI18n()
  const categoryMeta = $derived(CATEGORIES[category])
  const previewImage = $derived(page.blocks.find((block): block is SerializedPreviewImageBlock => block.type === 'PreviewImageBlock'))
  const tags = $derived(page.blocks.filter((block): block is SerializedTagsBlock => block.type === 'TagsBlock').flatMap((block) => block.tags))
  const detailHref = $derived(i18n.path(`/pages/${category}/${page.id}`))
</script>

<div class="page-card" {@attach expandClick}>
  <div class="page-card-image">
    {#if previewImage}
      <img class="page-card-photo" src={previewImage.image.src} srcset={previewImage.image.srcset} sizes="(max-width: 744px) 100vw, 400px" width={previewImage.image.width} height={previewImage.image.height} alt={previewImage.altText} loading={priority ? 'eager' : 'lazy'} fetchpriority={priority ? 'high' : undefined} />
    {/if}
    <span
      class={['page-card-icon', {
        '-variant-brand': categoryMeta.variant === 'brand',
        '-variant-warning': categoryMeta.variant === 'warning',
        '-variant-success': categoryMeta.variant === 'success',
      }]}
    >
      <Icon icon={categoryMeta.icon} />
    </span>
  </div>
  <div class="page-card-body">
    {#if tags.length > 0}
      <ul class="page-card-tags">
        {#each tags as tag (tag)}
          <TagPill text={i18n.t(`tags.${tag}`)} variant={categoryMeta.variant} />
        {/each}
      </ul>
    {/if}
    <svelte:element this={headingLevel} class="page-card-title">{page.title}</svelte:element>
    <p class="page-card-description">{page.description}</p>
    <a href={detailHref} {@attach inertia({ href: detailHref })} class="page-card-link">{i18n.t('page.readMore')} <MirroredIcon icon={faArrowRight} /></a>
  </div>
</div>

<style>
  .page-card {
    display: flex;
    flex-direction: column;
    border: var(--border-style-1);
    border-radius: var(--radius-5);
    overflow: hidden;
    cursor: pointer;
    transition: transform var(--transition-medium) ease;

    &:hover {
      transform: scale(var(--scale-hover));
    }

    &:focus-within {
      outline: var(--border-2) solid var(--color-variant-brand);
      outline-offset: 2px;
    }
  }

  .page-card-image {
    position: relative;
    aspect-ratio: 4 / 3;
    overflow: hidden;
  }

  .page-card-photo {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .page-card-icon {
    position: absolute;
    top: var(--space-5);
    inset-inline-start: var(--space-5);
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--radius-6);
    color: var(--color-white);
    font-size: var(--font-size-3);
  }

  .page-card-icon.-variant-brand {
    background-color: var(--color-variant-brand);
  }

  .page-card-icon.-variant-warning {
    background-color: var(--color-variant-warning);
  }

  .page-card-icon.-variant-success {
    background-color: var(--color-variant-success);
  }

  .page-card-body {
    flex: 1;
    padding: var(--space-8);
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  .page-card-tags {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-3);
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .page-card-title {
    font-size: var(--font-size-6);
    font-weight: var(--font-weight-3);
    line-height: var(--line-height-3);
    margin: 0;
  }

  .page-card-description {
    font-size: var(--font-size-4);
    color: var(--color-text-muted);
    line-height: var(--line-height-4);
    margin: 0;
  }

  /* The one real, individually focusable link in the card — kept small and
     unobtrusive since the whole card is clickable too (via the expandClick attachment),
     but screen reader / keyboard users get a clearly labeled, properly sized
     interactive target instead of one giant card-wide link. */
  :global(a.page-card-link) {
    margin-top: auto;
    padding-top: var(--space-4);
    font-size: var(--font-size-3);
    font-weight: var(--font-weight-2);
    color: var(--color-text-muted);
    text-decoration: underline;
    text-underline-offset: 2px;
    width: fit-content;
    transition: color var(--transition-medium);
  }

  :global(a.page-card-link:hover) {
    color: var(--color-text);
  }

  :global(a.page-card-link:focus-visible) {
    outline: none;
  }
</style>
